<script lang="ts">
    import { _ } from "svelte-i18n"
    import MedialibImage from "../../../widgets/MedialibImage.svelte"
    import MoreButton from "../../../widgets/MoreButton.svelte"

    let {
        topic,
        links,
    }: {
        topic: Topic
        links: TopicLink[]
    } = $props()

    type FilteredLink = TopicLink & { filteredPath: string }

    function extractBasePath(page: TopicLink["page"]): string | null {
        if (!page) return null
        if (typeof page === "string") return page
        if (Array.isArray(page) && page[0] && typeof page[0] === "string") return page[0]
        return null
    }

    function appendTopicFilter(path: string, slug: string): string {
        if (!path) return path
        const [withoutHash, hash = ""] = path.split("#")
        const parts = withoutHash.split("?")
        const basePath = parts.shift() ?? ""
        const query = parts.join("?")
        const entries = query
            .split("&")
            .map((part) => part.trim())
            .filter(Boolean)
            .map((part) => {
                const [rawKey, ...rawValueParts] = part.split("=")
                return [decodeURIComponent(rawKey || ""), rawValueParts.join("=")]
            })
            .filter(([key]) => key && key !== "filters_topic")

        entries.push(["filters_topic", encodeURIComponent(slug)])

        const queryString = entries.map(([key, value]) => `${encodeURIComponent(key)}=${value}`).join("&")
        const hashSuffix = hash ? `#${hash}` : ""
        return queryString ? `${basePath}?${queryString}${hashSuffix}` : `${basePath}${hashSuffix}`
    }

    const filteredLinks: FilteredLink[] = $derived(
        (links ?? [])
            .map((link) => {
                const basePath = extractBasePath(link.page)
                if (!basePath) return null
                return {
                    ...link,
                    filteredPath: appendTopicFilter(basePath, topic.slug),
                }
            })
            .filter((link): link is FilteredLink => Boolean(link))
    )
</script>

<li class="topic-el">
    <MedialibImage id={topic.primary_image} alt="topic img" class="default" maxWidth={550} />
    <MedialibImage id={topic.hover_image} alt="topic img" class="hover" maxWidth={550} />
    <div class="text-content">
        <h4 class="tag medium">{$_("general.topic")}</h4>
        <h3 class="tag big">{topic.title}</h3>
    </div>
    <ul class="hover-overlay">
        {#if filteredLinks.length}
            {#each filteredLinks as link}
                <li>
                    <MoreButton text={link.title} path={link.filteredPath} type="white" />
                </li>
            {/each}
        {/if}
    </ul>
</li>

<style lang="less" global>
    @import "../../../../css/variables.less";
    li.topic-el {
        height: 528px;
        position: relative;
        overflow: hidden;
        width: 100%;
        @media (max-width: 1700px) {
            width: 528px;
            min-width: 528px;
        }

        .tag.medium {
            text-transform: uppercase;
        }
        @media @max-mobile {
            width: 100vw;
            height: 100vw;
            min-width: 100vw;
        }
        img {
            position: absolute;
            height: 100%;
            width: 100%;
            object-fit: cover;
            transition: opacity 0.3s ease-in;
            &.default {
                z-index: 1;
            }
            &.hover {
                z-index: 0;
                opacity: 0;
            }
        }

        .text-content {
            transition: padding-bottom 0.3s ease-in;
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
            gap: 0.6rem;
            justify-content: flex-end;
            position: relative;
            z-index: 3;
            padding: 3.6rem 1.2rem;
        }

        .hover-overlay {
            position: absolute;
            opacity: 0;
            bottom: -100%;
            z-index: 4;
            width: 100%;
            display: flex;
            pointer-events: none;
            flex-direction: column;
            transition:
                opacity 0.3s ease-in,
                bottom 0.3s ease-in;
            li {
                border-top: 1px solid var(--bright-100);
                background: rgba(36, 34, 32, 0.5);
                backdrop-filter: blur(10px);
                padding: 0.4rem 1.2rem;
            }
        }
        &:hover {
            img {
                &.default {
                    z-index: 1;
                }
                &.hover {
                    z-index: 2;
                    opacity: 1;
                }
            }
            .hover-overlay {
                bottom: 0px;
                opacity: 1;
                pointer-events: all;
            }
            .text-content {
                padding-bottom: 200px !important;
            }
        }
    }
</style>
